import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Save } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { showSaveSuccess, showSaveError } from '@/lib/swal'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { loanRequestService } from '@/services/loanRequestService'
import { userService } from '@/services/userService'
import { employeesService } from '@/services/employeesService'
import type { LoanAccountItem, LoanLookupItem, LoanRequestVm } from '@/api/types'

function todayIso() {
  const d = new Date()
  return d.toISOString().slice(0, 10)
}

export const LoanRequestDetail: React.FC = () => {
  const navigate = useNavigate()
  const params = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0
  const userId = user?.userId ?? 0

  const isNew = params.id === 'new' || !params.id
  const editId = !isNew ? Number(params.id) : 0

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [loanTypes, setLoanTypes] = useState<LoanLookupItem[]>([])
  const [accounts, setAccounts] = useState<LoanAccountItem[]>([])

  const [loanTypeId, setLoanTypeId] = useState<number>(0)
  const [startDate, setStartDate] = useState<string>(todayIso())
  const [loanAmount, setLoanAmount] = useState<string>('0')
  const [noOfInstallment, setNoOfInstallment] = useState<string>('0')
  const [deductAmount, setDeductAmount] = useState<string>('0')
  const [creditAccount, setCreditAccount] = useState<number>(0)
  const [details, setDetails] = useState('')
  const [attachmentUrl, setAttachmentUrl] = useState<string>('')
  const [file, setFile] = useState<File | null>(null)

  useEffect(() => {
    if (!branchId) return
    const loadLookups = async () => {
      try {
        const [typeList, accList] = await Promise.all([
          loanRequestService.getLoanTypes(),
          loanRequestService.getAccounts(branchId),
        ])
        setLoanTypes(typeList || [])
        setAccounts(accList || [])
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? e?.message ?? 'Failed to load form lookups.')
      }
    }
    loadLookups()
  }, [branchId])

  useEffect(() => {
    if (!branchId || isNew || !editId) return
    const load = async () => {
      try {
        setLoading(true)
        const vm = await loanRequestService.getById(editId, branchId)
        if (!vm) return
        setLoanTypeId(vm.loanTypeId ?? 0)
        setStartDate(vm.startDate ? String(vm.startDate).slice(0, 10) : todayIso())
        setLoanAmount(String(vm.loanAmount ?? 0))
        setNoOfInstallment(String(vm.noOfInstallment ?? 0))
        setDeductAmount(String(vm.deductAmount ?? 0))
        setCreditAccount(vm.creditAccount ?? 0)
        setDetails(vm.details ?? '')
        setAttachmentUrl(vm.attachmentUrl ?? '')
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? e?.message ?? 'Failed to load loan request.')
        navigate('/self-services/loan-requests')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [branchId, isNew, editId, navigate])

  // Auto-calculate deduction per month = Loan Amount / No Of Installment
  useEffect(() => {
    const loan = Number(loanAmount) || 0
    const installments = Number(noOfInstallment) || 0
    if (installments > 0 && loan >= 0) {
      const perMonth = Math.round((loan / installments) * 100) / 100
      setDeductAmount(String(perMonth))
    }
  }, [loanAmount, noOfInstallment])

  const lookupLabel = (x: LoanLookupItem) =>
    x.lookupNameEn || x.lookupNameAr || x.nameEn || x.nameAr || x.name || String(x.codeType ?? x.id)

  const accountLabel = (a: LoanAccountItem) => {
    const name = a.accountName || a.nameEn || a.nameAr || ''
    const code = a.accountCode ? ` (${a.accountCode})` : ''
    return `${name}${code}`.trim() || `Account ${a.id}`
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!branchId) return
    if (!loanTypeId) {
      await showSaveError({ message: 'Loan Type is required.' })
      return
    }
    if (!creditAccount) {
      await showSaveError({ message: 'Credit Account is required.' })
      return
    }
    if (!startDate) {
      await showSaveError({ message: 'Loan Request Date is required.' })
      return
    }
    if (!details.trim()) {
      await showSaveError({ message: 'Details is required.' })
      return
    }

    const vm: LoanRequestVm = {
      id: isNew ? 0 : editId,
      startDate,
      loanAmount: Number(loanAmount || 0),
      noOfInstallment: Number(noOfInstallment || 0),
      deductAmount: Number(deductAmount || 0),
      loanTypeId,
      details,
      status: 0,
      branchId,
      creditAccount,
      attachmentUrl: attachmentUrl || undefined,
      empId: 0,
      departmentId: 0,
    }

    try {
      setSaving(true)

      // Build departmentId & empId from logged-in user (self-service flow)
      const u = userId ? await userService.getUserById(branchId, userId) : null
      const computedEmpId = u?.empId ?? u?.employeeId ?? 0
      if (!computedEmpId) {
        await showSaveError({
          message: 'Employee could not be determined. Please ensure your user is linked to an employee.',
        })
        return
      }

      const emp = await employeesService.getEmployeeById(branchId, computedEmpId)
      const computedDepartmentId = (emp as any)?.empDepartmetId ?? (emp as any)?.departmentId ?? 0
      if (!computedDepartmentId) {
        await showSaveError({ message: 'Department could not be determined from employee profile.' })
        return
      }

      vm.empId = computedEmpId
      vm.departmentId = computedDepartmentId

      if (isNew) {
        await loanRequestService.create(vm, file)
        await showSaveSuccess(false)
      } else {
        await loanRequestService.update(vm, file)
        await showSaveSuccess(true)
      }
      navigate('/self-services/loan-requests')
    } catch (err: any) {
      await showSaveError(err?.response?.data?.message ?? err?.message ?? 'Failed to save.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Loan Request"
        description="Loan Request Detail"
      />

      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent className="p-6 space-y-6 overflow-visible">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <SearchableSelect
                  label="Loan Type *"
                  options={[
                    { value: '0', label: 'Select' },
                    ...loanTypes.filter((x) => x.id).map((x) => ({
                      value: String(x.id),
                      label: lookupLabel(x),
                    })) as SearchableSelectOption[],
                  ]}
                  value={loanTypeId ? String(loanTypeId) : '0'}
                  onValueChange={(v) => setLoanTypeId(Number(v) || 0)}
                  placeholder="Select"
                  searchPlaceholder="Search loan type..."
                  emptyMessage="No loan type found"
                />
              </div>

              <div className="space-y-2">
                <Label>Loan Request Date *</Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>

              <div className="space-y-2">
                <Label>Loan Amount *</Label>
                <Input value={loanAmount} onChange={(e) => setLoanAmount(e.target.value)} />
              </div>

              <div className="space-y-2">
                <Label>No Of Installment *</Label>
                <Input value={noOfInstallment} onChange={(e) => setNoOfInstallment(e.target.value)} />
              </div>

              <div className="space-y-2">
                <Label>Deduction per Month Amount *</Label>
                <Input
                  value={deductAmount}
                  onChange={(e) => setDeductAmount(e.target.value)}
                  title="Auto-calculated from Loan Amount ÷ No Of Installment (editable)"
                />
              </div>

              <div className="space-y-2">
                <SearchableSelect
                  label="Credit Account *"
                  options={[
                    { value: '0', label: 'Select' },
                    ...accounts.filter((a) => a.id).map((a) => ({
                      value: String(a.id),
                      label: accountLabel(a),
                    })) as SearchableSelectOption[],
                  ]}
                  value={creditAccount ? String(creditAccount) : '0'}
                  onValueChange={(v) => setCreditAccount(Number(v) || 0)}
                  placeholder="Select"
                  searchPlaceholder="Search account..."
                  emptyMessage="No account found"
                />
              </div>

              <div className="space-y-2">
                <Label>Attachment</Label>
                <Input
                  type="file"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
                {attachmentUrl ? (
                  <p className="text-xs text-muted-foreground">Existing: {attachmentUrl}</p>
                ) : null}
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>Details *</Label>
                <Textarea value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Details" />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" disabled={saving || loading} onClick={() => navigate('/self-services/loan-requests')}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving || loading}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : isNew ? 'Save' : 'Update'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  )
}

